import { useState } from "react";
import { Check, X } from "lucide-react";
import { STATUS_LABEL, STATUS_TONE, boxInput, daysOf, summarize } from "../../lib/format";

const dayName = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit" });

export const AttendanceSummary = ({ records }) => {
  const s = summarize(records);
  return (
    <div className="grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-4" data-testid="attendance-summary">
      {Object.entries(STATUS_LABEL).map(([k, l]) => (
        <div key={k} className="bg-[var(--ink)] p-4">
          <p className="overline text-[var(--bone-2)]">{l}</p>
          <p className="mt-1 font-display text-4xl font-black" data-testid={`summary-${k}`}>{s[k]}</p>
        </div>
      ))}
    </div>
  );
};

const Pill = ({ status }) => (
  <span className={`inline-block rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>
);

const EditRow = ({ date, rec, onSave, onClear }) => {
  const [f, setF] = useState({ status: rec?.status || "present", check_in: rec?.check_in || "", check_out: rec?.check_out || "", note: rec?.note || "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = () => onSave(date, { ...f, check_in: f.check_in || null, check_out: f.check_out || null });
  return (
    <li className={`grid grid-cols-2 items-center gap-2 py-3 sm:grid-cols-[90px_130px_110px_110px_1fr_auto] ${rec ? "" : "opacity-60 focus-within:opacity-100"}`} data-testid={`att-row-${date}`}>
      <span className="font-mono text-xs text-[var(--bone-2)]">{dayName(date)}</span>
      <select value={f.status} onChange={set("status")} className={boxInput} data-testid={`att-status-${date}`}>
        {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input type="time" value={f.check_in} onChange={set("check_in")} className={boxInput} data-testid={`att-in-${date}`} />
      <input type="time" value={f.check_out} onChange={set("check_out")} className={boxInput} data-testid={`att-out-${date}`} />
      <input value={f.note} onChange={set("note")} placeholder="Note" className={boxInput} data-testid={`att-note-${date}`} />
      <span className="flex gap-1">
        <button onClick={save} title="Save" className="rounded-full bg-[var(--signal)] p-2 text-[var(--ink)]" data-testid={`att-save-${date}`}><Check className="h-4 w-4" /></button>
        {rec && <button onClick={() => onClear(date)} title="Clear" className="rounded-full border border-white/20 p-2" data-testid={`att-clear-${date}`}><X className="h-4 w-4" /></button>}
      </span>
    </li>
  );
};

export const AttendanceTable = ({ month, records, editable = false, onSave, onClear }) => {
  const map = Object.fromEntries((Array.isArray(records) ? records : []).map((r) => [r.date, r]));
  const today = new Date().toLocaleDateString("en-CA");
  const days = daysOf(month).filter((d) => editable || d <= today);
  return (
    <ul className="divide-y divide-white/10 border-y border-white/10" data-testid="attendance-table">
      {days.map((d) =>
        editable ? (
          <EditRow key={`${d}-${map[d]?.id ?? "x"}-${map[d]?.status ?? ""}`} date={d} rec={map[d]} onSave={onSave} onClear={onClear} />
        ) : (
          <li key={d} className="grid grid-cols-[80px_1fr_auto] items-center gap-3 py-3 text-sm" data-testid={`att-row-${d}`}>
            <span className="font-mono text-xs text-[var(--bone-2)]">{dayName(d)}</span>
            <span className="text-[var(--bone-2)]">{map[d] ? `${map[d].check_in || "--:--"} → ${map[d].check_out || "--:--"}` : "—"}</span>
            {map[d] ? <Pill status={map[d].status} /> : <span className="font-mono text-[11px] text-[#8f8a7e]">NOT MARKED</span>}
          </li>
        ),
      )}
    </ul>
  );
};
